<template>
  <div class="node">
    <div class="mc-card">
      <div class="mc-card__header">
        <span class="mc-card__title">{{ node.data.stateId || 'Estado' }}</span>

        <!-- Badges -->
        <span
          v-if="isStart"
          class="mc-pill mc-pill--start"
        >
          <i class="feather icon-play mr-25" />Start
        </span>
        <span
          v-if="hasEnd"
          class="mc-pill mc-pill--end"
        >
          <i class="feather icon-x-circle mr-25" />Encerrar
        </span>
        <span
          v-if="hasCentral"
          class="mc-pill mc-pill--central"
        >
          <i class="feather icon-life-buoy mr-25" />Central
        </span>
      </div>

      <div class="mc-card__content">
        <!-- Mostra o preview apenas se existir texto -->
        <div
          v-if="previewText"
          class="mc-text"
        >
          {{ previewText }}
        </div>

        <div
          v-if="chips.length"
          class="mc-actions"
        >
          <div
            v-for="(t, i) in chips"
            :key="i"
            class="mc-chip"
            :title="t"
          >
            {{ t }}
          </div>
        </div>
      </div>

      <!-- Inputs -->
      <div
        v-for="input in inputsList"
        :key="'in-'+input.key"
        class="input mc-io mc-io--in"
      >
        <Socket
          v-if="input && input.socket && input.socket.name"
          v-socket:input="input"
          :io="input"
          :input="input"
          :socket="input.socket"
          :type="'input'"
        />
      </div>

      <!-- Outputs -->
      <div
        v-for="output in outputsList"
        :key="'out-'+output.key"
        class="output mc-io mc-io--out"
      >
        <Socket
          v-if="output && output.socket && output.socket.name"
          v-socket:output="output"
          :io="output"
          :output="output"
          :socket="output.socket"
          :type="'output'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VueRenderPlugin from 'rete-vue-render-plugin'

export default {
  name: 'ReteStateNode',
  components: {
    Socket: VueRenderPlugin.Socket,
    Control: VueRenderPlugin.Control,
  },
  mixins: [VueRenderPlugin.mixin],
  props: ['node', 'bindSocket', 'bindControl'],

  computed: {
    /* Badges */
    isStart() {
      return !!(this.node?.data?._isStart || this.node?.data?.isStart)
    },
    hasEnd() {
      const acts = Array.isArray(this.node?.data?.actions) ? this.node.data.actions : []
      return acts.some(a => a && a.action === 'end')
    },
    hasCentral() {
      const acts = Array.isArray(this.node?.data?.actions) ? this.node.data.actions : []
      return acts.some(a => a && a.action === 'set_mode' && String(a.mode || '').toLowerCase() === 'central')
    },

    /* IO lists (compat) */
    inputsList() {
      try { return this.inputs() } catch (_) {
        const map = this.node && this.node.inputs ? this.node.inputs : null
        return map ? Array.from(map.values()) : []
      }
    },
    outputsList() {
      try { return this.outputs() } catch (_) {
        const map = this.node && this.node.outputs ? this.node.outputs : null
        return map ? Array.from(map.values()) : []
      }
    },

    /* Preview */
    previewText() {
      const acts = Array.isArray(this.node?.data?.actions) ? this.node.data.actions : []
      if (!acts.length) return '' // sem ações, sem preview
      const st = acts.find(a => a && a.action === 'send_text')
      const sb = acts.find(a => a && a.action === 'send_buttons')
      const sl = acts.find(a => a && a.action === 'send_list')
      const text = (st && (st.text || st.body)) || (sb && sb.body) || (sl && sl.body) || ''
      const s = String(text || '')
      if (!s.trim()) return '' // evita fallback para "send_text"
      return s.length <= 160 ? s : (`${s.slice(0, 159)}…`)
    },

    /* Chips de botões/opções */
    chips() {
      const acts = Array.isArray(this.node?.data?.actions) ? this.node.data.actions : []
      const out = []
      const sb = acts.find(a => a && a.action === 'send_buttons')
      if (sb?.buttons?.length) out.push(...sb.buttons.map(b => (b && b.title) || 'button'))
      const sl = acts.find(a => a && a.action === 'send_list')
      if (sl) {
        if (Array.isArray(sl.items)) out.push(...sl.items.map(it => (it && (it.label || it.title)) || 'option'))
        if (sl.sections?.[0]?.rows?.length) out.push(...sl.sections[0].rows.map(r => (r && (r.title || r.label)) || 'option'))
      }
      return out.slice(0, 12)
    },
  },
}
</script>

  <style scoped>
  .mc-card{
    position:relative;
    background:#fff;
    border:1px solid #e6ebf2;
    border-radius:20px;
    min-width:420px;
    max-width:480px;
    box-shadow:0 12px 30px rgba(16,24,40,.10);
    padding:0;
    cursor: pointer;
  }
  :deep(.selected .mc-card){ box-shadow:0 14px 34px rgba(16,24,40,.16); }

  .mc-card__header{
    display:flex; align-items:center; gap:8px; padding:16px 18px 8px 18px; flex-wrap:wrap;
  }
  .mc-card__title{ font-weight:800; font-size:20px; color:#111827; }

  .mc-pill{ font-size:11px; font-weight:800; padding:2px 8px; border-radius:999px; display:inline-flex; align-items:center; gap:6px; }
  .mc-pill--start{ background:#e7f5ff; color:#1e40af; border:1px solid #dbeafe; }
  .mc-pill--end{ background:#fff1f2; color:#b91c1c; border:1px solid #fecaca; }
  .mc-pill--central{ background:#f1f0ff; color:#6d28d9; border:1px solid #ddd6fe; }
  .mr-25{ margin-right:.25rem }

  .mc-card__content{ padding:8px 18px 16px 18px; }
  .mc-text{
    color:#6b7280; background:#f8fafc;
    border:1px solid #eef2f7; border-radius:12px;
    padding:10px 12px; font-weight:600; margin-bottom:10px;
  }
  .mc-actions{ display:flex; flex-direction:column; gap:10px; }
  .mc-chip{
    text-align:center; padding:10px 12px; border-radius:10px;
    border:1.5px solid #c7d2fe; background:#f8fafc; color:#1f2937; font-weight:600;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }

  /* Sockets (laterais) */
  .mc-io{ position:absolute; top:50%; transform:translateY(-50%); }
  .mc-io--in{ left:-6px; }
  .mc-io--out{ right:-6px; }
  :deep(.socket){
    width:8px; height:8px; border-radius:999px;
    background:#111827; border:2px solid #fff;
    box-shadow:0 2px 4px rgba(0,0,0,.12);
  }
  </style>
